// ============================================
// components/Layout.tsx
//
// 全局框架: 顶部导航栏 + 内容区 + 底部.
// 所有"完整页面"(首页 / 详情 / 主页 / 发布)都套这层,
// 保证跟官网 hipiido_web_v3.2.1 视觉一致.
//
// 导航栏:
//   - 左: logo (.logo-mark 渐变文字) + "社区" 副标
//   - 中: 搜索框 (回车 → /search?q=, Week 2 后续接搜索页, 先占位跳首页带 query)
//   - 右: 登录态. 已登录显示头像+昵称(点进我的主页); 未登录显示"回 Studio 发布"
//
// FromApp / NotFound 这类独立页不套 Layout (它们是全屏卡片).
// ============================================

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, LogOut } from 'lucide-react';
import { getCachedUser, isLoggedIn, refreshCurrentUser, logout } from '../services/auth';
import Avatar from './Avatar';
import NotificationBell from './NotificationBell';
import LoginModal from './LoginModal';
import { LOGIN_PROMPT_EVENT } from '../services/loginPrompt';
import type { UserInfo } from '../types/community';

interface Props {
  children: React.ReactNode;
  /** 是否在导航栏显示搜索框, 默认 true */
  showSearch?: boolean;
  /** 内容区是否用窄容器 (详情/发布页用 max-w-3xl, 首页用 max-w-6xl) */
  width?: 'wide' | 'narrow';
  /** 嵌入模式: 隐藏顶部导航栏 (审核预览 iframe 用) */
  embed?: boolean;
}

export default function Layout({ children, showSearch = true, width = 'wide', embed = false }: Props) {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [q, setQ] = useState('');
  const [showLoginHint, setShowLoginHint] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // 点击外部关闭用户菜单
  useEffect(() => {
    if (!showUserMenu) return;
    const onDown = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showUserMenu]);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    setUser(null);
    navigate('/');
  };

  // 监听全局"要求登录"事件：任何页面调 requireLogin() / openLoginModal() 都弹登录框
  useEffect(() => {
    const onRequireLogin = () => setShowLoginHint(true);
    window.addEventListener(LOGIN_PROMPT_EVENT, onRequireLogin);
    return () => window.removeEventListener(LOGIN_PROMPT_EVENT, onRequireLogin);
  }, []);

  useEffect(() => {
    if (!isLoggedIn()) return;
    // 先用缓存即时显示, 再总是刷新一次拿最新头像/昵称/short_id
    setUser(getCachedUser());
    refreshCurrentUser().then((fresh) => {
      if (fresh) setUser(fresh);
    }).catch(() => { /* 刷新失败保持缓存 */ });
  }, []);

  function onSearch(e: React.FormEvent) {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    // Week 2 后续接专门搜索页; 现在先跳首页带 query (首页可读取做前端过滤兜底)
    navigate(`/?q=${encodeURIComponent(term)}`);
  }

  const containerW = width === 'narrow' ? 'max-w-3xl' : 'max-w-6xl';

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      {/* ───── 顶部导航栏 (embed 模式隐藏) ───── */}
      {!embed && (
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-ink-300/40">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center gap-4">
          {/* logo — 对齐官网形式: 笑脸图标 + HiPiiDo 创新工场 + MAKERS CLUB 副标 */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <img
              src="https://hipiido.cloud/images/logo-transparent.png"
              alt="HiPiiDo"
              className="h-10 w-auto object-contain"
              onError={(e) => {
                const img = e.currentTarget;
                img.style.display = 'none';
                const fallback = img.nextElementSibling as HTMLElement | null;
                if (fallback) fallback.style.display = 'grid';
              }}
            />
            <span
              className="grid place-items-center w-10 h-10 rounded-xl bg-gradient-to-br from-gold-600 to-gold-400 text-white font-black text-lg shadow-card"
              style={{ display: 'none' }}
            >
              H
            </span>
            <span className="leading-none">
              <span className="block text-xl font-black tracking-tight">
                <span className="text-ink-900">HiPiiDo</span>
                <span className="text-gold-600"> 创新工场</span>
              </span>
              <span className="block text-[9px] font-semibold tracking-[0.2em] text-ink-400 uppercase mt-0.5">
                Makers Club
              </span>
            </span>
          </Link>

          {/* 搜索框 */}
          {showSearch && (
            <form onSubmit={onSearch} className="flex-1 max-w-md mx-auto hidden sm:block">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 text-sm">
                  ⌕
                </span>
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="搜索作品、作者、标签…"
                  className="input-field pl-9 py-2 text-sm bg-paper border-ink-300/60"
                />
              </div>
            </form>
          )}

          {!showSearch && <div className="flex-1" />}

          {/* 登录态 */}
          <div className="shrink-0 flex items-center gap-1">
            {user && <NotificationBell />}
            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu((v) => !v)}
                  className="flex items-center gap-2 group"
                >
                  <Avatar name={user.nickname} size={32} className="!rounded-full border border-ink-300/50" />
                  <span className="text-sm font-medium text-ink-700 group-hover:text-gold-600 transition-colors hidden md:inline max-w-[8rem] truncate">
                    {user.nickname || '我的主页'}
                  </span>
                </button>
                {/* 下拉菜单 */}
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-40 bg-white rounded-xl shadow-card border border-ink-300/40 overflow-hidden z-[60]">
                    <Link
                      to={user.short_id ? `/u/${user.short_id}` : '/'}
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-2.5 px-4 py-3 text-sm text-ink-700 hover:bg-paper transition-colors"
                    >
                      <User className="w-4 h-4 text-ink-400" />
                      我的主页
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-ink-700 hover:bg-paper transition-colors border-t border-ink-300/30"
                    >
                      <LogOut className="w-4 h-4 text-ink-400" />
                      退出登录
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowLoginHint(true)}
                className="btn-primary text-sm whitespace-nowrap"
              >
                登录
              </button>
            )}
          </div>
        </div>
      </header>
      )}

      {/* ───── 登录弹窗（未登录点"登录"时弹出，真正的用户名+密码登录） ───── */}
      {showLoginHint && (
        <LoginModal onClose={() => setShowLoginHint(false)} />
      )}

      {/* ───── 内容区 ───── */}
      <main className="flex-1 w-full">
        <div className={`${containerW} mx-auto px-4 w-full`}>{children}</div>
      </main>

      {/* ───── 底部 ───── */}
      <footer className="border-t border-ink-300/40 bg-white mt-16">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div>
              <div className="logo-mark text-lg">HiPiiDo 创新工场</div>
              <p className="text-sm text-ink-500 mt-1.5">
                硬件创客的作品分享平台 · 把 Studio 里搭的积木分享给大家
              </p>
            </div>
            <div className="flex items-center gap-6 text-sm text-ink-500">
              <a
                href="https://hipiido.cloud/"
                className="hover:text-gold-600 transition-colors"
              >
                官网
              </a>
              <Link to="/" className="hover:text-gold-600 transition-colors">
                作品广场
              </Link>
              <span className="text-ink-400 font-mono text-xs">v3.5</span>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-ink-300/30 text-xs text-ink-400 font-mono">
            © {new Date().getFullYear()} HiPiiDo · Made for makers
          </div>
        </div>
      </footer>
    </div>
  );
}
